"use client";

import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import OperatorsTableActionMainRule from "./OperatorsTableActionMainRule";
import CustomChip from "../CustomChip/CustomChip";
import useFetchWithAuth from "../../features/hooks/useFetchWithAuth";
import StyledTableCell from "../ui/TableCell/TableCell";

const headers = [
  "Місце",
  "Ім’я",
  "Прийнятих",
  "Без допродажу",
  "Кількість кросів",
  "Маржа допродажу",
  "Середній чек",
  "% допродажів",
  "ЗП оператора",
  "Комбо днів",
];

const options = [
  { value: "accepted_count", label: "Прийнятих" },
  { value: "orders_without_upsell", label: "Без допродажу" },
  { value: "cross_count", label: "Кількість кросів" },
  { value: "upsell_margin_sum", label: "Маржа допродажу" },
  { value: "avg_check", label: "Середній чек" },
  { value: "upsell_percent", label: "% допродажів" },
  { value: "operator_salary", label: "ЗП оператора" },
  { value: "combo_days", label: "Комбо днів" },
];

export default function RatingTableContainer() {
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");

  const days = 1;
  const endpoint = `/statistic/operator/ratings/?days=${days}`;

  const { data: apiResponse, loading, error } = useFetchWithAuth(endpoint);

  const operators = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
  const normalizedOperators = useMemo(() => {
    return operators.map((op) => ({
      ...op,
      full_name:
        (op.full_name ??
          `${op.first_name || ""} ${op.last_name || ""}`.trim()) ||
        op.username ||
        "-",
      accepted_count: op.accepted_count ?? 0,
      orders_without_upsell: op.orders_without_upsell ?? 0,
      cross_count: op.cross_count ?? 0,
      upsell_margin_sum: op.upsell_margin_sum ?? 0,
      avg_check: op.avg_check ?? op.average_check ?? 0,
      upsell_percent: op.upsell_percent ?? op.percent_upsell ?? 0,
      operator_salary:
        op.operator_salary ?? op.salary_amount ?? op.salary ?? 0,
    }));
  }, [operators]);
  const filteredOperators = useMemo(() => {
    return normalizedOperators.filter((op) => {
      const values = [
        op.accepted_count,
        op.orders_without_upsell,
        op.cross_count,
        op.upsell_margin_sum,
        op.avg_check,
        op.upsell_percent,
        op.operator_salary,
        op.combo_days,
      ];
      return values.some((value) => Number(value) || 0);
    });
  }, [normalizedOperators]);
  const visibleOperators =
    filteredOperators.length > 0 ? filteredOperators : normalizedOperators;

  const handleSortOrderClick = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const sortedOperators = [...visibleOperators].sort((a, b) => {
    if (!sortBy) return 0;
    const valA = a[sortBy] ?? 0;
    const valB = b[sortBy] ?? 0;

    if (typeof valA === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    } else {
      return sortOrder === "asc" ? valA - valB : valB - valA;
    }
  });

  const handleResetFilters = () => {
    setSortBy("");
    setSortOrder("desc");
  };

  return (
    <>
      <Card sx={{ p: 2 }}>
        <OperatorsTableActionMainRule
          title="Рейтинг операторів"
          options={options}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          sortOrderLabel={sortOrder === "asc" ? "Зростання" : "Спадання"}
          onSortOrderClick={handleSortOrderClick}
          onResetFilters={handleResetFilters}
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {headers.map((header, idx) => (
                  <TableCell className="text-black border-bottom" key={idx}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={10}>Завантаження...</TableCell>
                </TableRow>
              )}
              {error && (
                <TableRow>
                  <TableCell colSpan={10}>Помилка завантаження даних</TableCell>
                </TableRow>
              )}
              {!loading &&
                !error &&
                sortedOperators.map((op, idx) => (
                  <TableRow key={idx} className="border-bottom">
                    <StyledTableCell>{idx + 1}</StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.full_name ?? "-"}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.accepted_count ?? 0}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.orders_without_upsell ?? 0}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.cross_count ?? 0}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.upsell_margin_sum ?? 0}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.avg_check ?? 0}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {`${op.upsell_percent ?? 0}%`}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.operator_salary ?? "-"}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography sx={{ color: "#16a34a" }}>
                        {op.combo_days ?? 0}
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </>
  );
}
